import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Fade, IconButton, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import { VyIcon } from '@vy/ui-design-system';
import { IdShortener } from '@vy/services';
import { useGetCreditNotePdf } from '../../../../../../hooks/use-get-credit-note-pdf';
import { useMenu } from '../../../../../../hooks/use-menu';
import { saveFileAsPdf } from '../../../../../../services/file-saver-as-pdf';
import { runMutation } from '../../../../../../utils/vy-mutation';
import { useExportNetSuiteCreditMemo } from '../../../../../../hooks/requests/integrations';
import { userMessage } from '../../../../../../utils/vy-toasts';
import { isError } from '@vy/utils';
import { INTEGRATION_PROVIDERS } from '@vy/types';
import { useActivatedIntegration } from '../../../../../../hooks/use-activated-integration';

type CreditLedgerTableDotMenuProps = {
  creditLedgerId: string;
  downloadCreditNoteDisabled: boolean;
  enableSyncToNetSuite?: boolean;
};

export const CreditLedgerTableDotMenu: React.FC<CreditLedgerTableDotMenuProps> = ({
  creditLedgerId,
  downloadCreditNoteDisabled,
  enableSyncToNetSuite = false,
}) => {
  const { isOpen, handleMenuOpen, handleMenuClose, anchorElement } = useMenu();
  const downloadCreditNotePdfMutation = useGetCreditNotePdf();
  const exportNetSuiteCreditMemoMutation = useExportNetSuiteCreditMemo();
  const isNetSuiteEnabled = useActivatedIntegration(INTEGRATION_PROVIDERS.netSuite);
  const enableErpSync = isNetSuiteEnabled && enableSyncToNetSuite;

  const handleDownloadInvoicePdf = async () => {
    handleMenuClose();
    await runMutation(
      downloadCreditNotePdfMutation,
      {
        creditLedgerId,
      },
      {
        pending: 'Downloading credit note...',
        success: 'Credit note downloaded',
        error: 'Failed to download credit note',
      },
      {
        onSuccess: async ({ encodedPdf }) => {
          await saveFileAsPdf(encodedPdf, `CreditNote-${IdShortener(creditLedgerId)}`);
        },
      },
    );
  };

  const handleSyncCreditLedger = async () => {
    await runMutation(
      exportNetSuiteCreditMemoMutation,
      creditLedgerId,
      {
        pending: 'Exporting credit memo to NetSuite...',
        success: 'Credit memo exported to NetSuite',
        error: 'Failed to export credit memo to NetSuite',
      },
      {
        onError: (error) => {
          if (isError(error)) {
            userMessage('error', error.message);
          }
        },
      },
    );
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    handleMenuOpen(event);
  };

  return (
    <>
      <IconButton onClick={handleMenuClick}>
        <MoreVertIcon />
      </IconButton>
      <Menu
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={isOpen}
        anchorEl={anchorElement}
        onClose={handleMenuClose}
        slots={{
          transition: Fade
        }}
      >
        <MenuItem onClick={() => handleDownloadInvoicePdf()} disabled={downloadCreditNoteDisabled}>
          <ListItemIcon>
            <VyIcon
              icon="Download"
              color="black"
              style={{
                transform: 'scale(0.7)',
              }}
            />
          </ListItemIcon>
          <ListItemText>Download</ListItemText>
        </MenuItem>

        {enableErpSync && (
          <MenuItem onClick={() => handleSyncCreditLedger()}>
            <ListItemIcon>
              <VyIcon
                icon="Synchronize"
                color="black"
                style={{
                  transform: 'scale(0.7)',
                }}
              />
            </ListItemIcon>
            <ListItemText>Sync To NetSuite</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </>
  );
};
